"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { ChevronDown } from "lucide-react";
import { setTimeframeCall } from "../services/settingsService";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { Loader2 } from "lucide-react";

const SelectTimeframe = ({ timeFrame, type }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { updateSettings, userProfile } = useAuth();
  const { toast } = useToast();
  const [selectedTimeframe, setSelectedTimeframe] = useState(timeFrame);
  const timeframes = useMemo(() => ["Monthly", "Daily", "Weekly"], []);

  const selectTime = useCallback(
    async (data) => {
      setIsLoading(true);
      try {
        const authId = userProfile?.authId;
        const apiCall = await setTimeframeCall(data.toLowerCase(), authId);
        if (apiCall.status === 200 && apiCall.data.status === true) {
          const newSettings = apiCall.data.data;
          setSelectedTimeframe(data);
          updateSettings(newSettings);
          setIsLoading(false);
          toast({
            description: "Timeframe Successfully Updated",
            variant: "eighty",
          });
        } else {
          setIsLoading(false);
          console.error("Timeframe update failed:", apiCall);
          toast({
            description: apiCall?.data?.message || "Error Updating Timeframe",
            variant: "destructive",
          });
        }
      } catch (err) {
        setIsLoading(false);
        console.error("Timeframe update error:", err);
        toast({
          description:
            err?.response?.data?.message || "Error Updating Timeframe",
          variant: "destructive",
        });
      }
    },
    [toast, updateSettings, userProfile?.authId]
  );

  useEffect(() => {
    if (timeframes && timeframes.length > 0 && !selectedTimeframe) {
      const defaultTf = timeframes.find((tf) => tf === timeFrame);
      setSelectedTimeframe(defaultTf);
      selectTime(defaultTf);
    }
  }, [selectedTimeframe, selectTime, timeFrame, timeframes]);
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className="relative w-30 h-10 rounded-full bg-transparent  border border-gray-100 border-opacity-35 text-text-secondary  flex justify-between px-4"
        >
          {isLoading ? (
            <Loader2 className="animate-spin text-gray-700" />
          ) : (
            <>
              <span className="text-sm font-semibold">
                {selectedTimeframe.charAt(0).toUpperCase() +
                  selectedTimeframe.slice(1)}
              </span>
              <ChevronDown className="w-4 h-4 text-gray-500 " />
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md p-6 rounded-lg shadow-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-700 mt-5">
            Select a timeframe
          </DialogTitle>
          <p className="text-sm text-gray-500">
            Select the timeframe to measure and compare your portfolio's
            performance.
          </p>
        </DialogHeader>
        <div className="mt-4 max-h-[200px]">
          <DialogClose className="w-full space-y-2">
            {timeframes.map((timeframe) => (
              <div
                key={timeframe}
                onClick={() => selectTime(timeframe)}
                className={`flex items-center justify-between px-4 py-3 cursor-pointer border rounded-md ${
                  selectedTimeframe.charAt(0).toUpperCase() +
                    selectedTimeframe.slice(1) ===
                  timeframe
                    ? "border-rose-500"
                    : "border-gray-200"
                } hover:bg-gray-100`}
              >
                <span className="text-sm font-medium text-gray-800">
                  {timeframe}
                </span>
              </div>
            ))}
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SelectTimeframe;
